import type { AxiosError, AxiosRequestConfig, Method } from 'axios'
import { apiClient } from '@/services/api/axios'
import type { ApiEnvelope, HttpResult, PaginatedData, PaginationMeta } from '@/types/api.types'
import { AppError, normalizeApiError } from '@/services/api/errors'

function unwrapEnvelope<T>(envelope: ApiEnvelope<T>): HttpResult<T> {
  if (!envelope || typeof envelope !== 'object') {
    throw new AppError('Invalid server response.', 'UNEXPECTED')
  }

  if (!envelope.success) {
    throw new AppError(envelope.message || 'Request failed', 'UNEXPECTED', {
      details: envelope.errors,
    })
  }

  return {
    data: envelope.data as T,
    message: envelope.message,
  }
}

async function request<T>(
  method: Method,
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> {
  try {
    const res = await apiClient.request<ApiEnvelope<T>>({
      ...config,
      method,
      url,
      // Prefer explicit body; keep config.data for DELETE bodies (FCM / push unsubscribe).
      data: body !== undefined ? body : config?.data,
    })
    return unwrapEnvelope(res.data)
  } catch (error) {
    throw normalizeApiError(error)
  }
}

/** Returns unwrapped `data` (may be undefined for empty success). */
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await request<T>('GET', url, undefined, config)
  return data as T
}

export async function apiPost<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await request<T>('POST', url, body, config)
  return data as T
}

export async function apiPut<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await request<T>('PUT', url, body, config)
  return data as T
}

export async function apiPatch<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await request<T>('PATCH', url, body, config)
  return data as T
}

export async function apiDelete<T = void>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await request<T>('DELETE', url, undefined, config)
  return data as T
}

/** Same as helpers but also returns the envelope message (for toasts). */
export async function apiGetResult<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> {
  return request<T>('GET', url, undefined, config)
}

export async function apiPostResult<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> {
  return request<T>('POST', url, body, config)
}

export async function apiPutResult<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> {
  return request<T>('PUT', url, body, config)
}

export async function apiPatchResult<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> {
  return request<T>('PATCH', url, body, config)
}

export async function apiDeleteResult<T = void>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> {
  return request<T>('DELETE', url, undefined, config)
}

export function ok<T>(data: T, message?: string) {
  return { success: true as const, data, message }
}

export function fail(
  message: string,
  options?: { errors?: Record<string, string[]>; status?: number; code?: string },
) {
  return { success: false as const, message, ...options }
}

export function buildPaginationMeta(
  total: number,
  page: number,
  pageSize: number,
): PaginationMeta {
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1)
  return { page, pageSize, total, totalPages }
}

export function paginateItems<T>(
  items: T[],
  page: number,
  pageSize: number,
): PaginatedData<T> {
  const start = (page - 1) * pageSize
  const slice = items.slice(start, start + pageSize)
  return {
    items: slice,
    meta: buildPaginationMeta(items.length, page, pageSize),
  }
}

export function isAxiosError(error: unknown): error is AxiosError {
  return Boolean(error) && typeof error === 'object' && 'isAxiosError' in (error as object)
}

export interface BlobDownload {
  blob: Blob
  filename: string
}

function filenameFromDisposition(header?: string): string | null {
  if (!header) return null
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim())
    } catch {
      return utfMatch[1].trim()
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(header)
  return plainMatch?.[1]?.trim() ?? null
}

/**
 * Binary download (reports PDF/XLSX). Not a JSON envelope.
 * On HTTP errors, parses a JSON error blob when present.
 * Pass `method: 'POST'` + `data` (e.g. FormData) when the export needs a body.
 */
export async function apiDownloadBlob(
  url: string,
  config?: AxiosRequestConfig & { fallbackFilename?: string },
): Promise<BlobDownload> {
  try {
    const res = await apiClient.request<Blob>({
      ...config,
      method: config?.method ?? 'GET',
      url,
      responseType: 'blob',
    })
    const filename =
      filenameFromDisposition(res.headers['content-disposition']) ??
      config?.fallbackFilename ??
      'download'
    return { blob: res.data, filename }
  } catch (error) {
    if (isAxiosError(error) && error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text()
        const parsed = JSON.parse(text) as {
          success?: boolean
          message?: string
          errors?: Record<string, string[]>
        }
        throw new AppError(parsed.message || 'Download failed', 'UNEXPECTED', {
          status: error.response.status,
          details: parsed.errors,
          original: error,
        })
      } catch (inner) {
        if (inner instanceof AppError) throw inner
      }
    }
    throw normalizeApiError(error)
  }
}

export function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export { AppError, normalizeApiError }
